import { useEffect, useRef } from 'react';
import styles from './checkbox.module.css';

const { checkbox, input, label } = styles;

interface CheckboxProps {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  ref?: React.Ref<HTMLInputElement>;
  className?: string;
  /**
   * HAT-1212: מצב חלקי — חלק מהשורות נבחרו אבל לא כולן. יש למצב הזה סמן
   * DOM נפרד (input.indeterminate) שאינו attribute ולכן חייב להיקבע ב-JS,
   * אבל הוא כן מקבל תצוגה של קו במקום ✓ בברירת המחדל של הדפדפן. עדיין
   * נשלט על ידינו כי ה-Checkbox של האתר מסתיר את ה-input ומצייר span,
   * ולכן הוספנו גם :indeterminate CSS.
   */
  indeterminate?: boolean;
  /**
   * Accessible naming and validation state for a checkbox that is not wrapped in a <label> of its
   * own - for instance a consent row whose text holds a link, which cannot sit inside a label
   * without hijacking the link's click.
   */
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  ariaInvalid?: boolean;
  ariaRequired?: boolean;
}

export default function Checkbox({
  checked,
  onCheckedChange,
  disabled,
  ref,
  className,
  indeterminate,
  ariaLabelledBy,
  ariaDescribedBy,
  ariaInvalid,
  ariaRequired,
}: CheckboxProps) {
  /*
   * indeterminate הוא property של HTMLInputElement שאין לו attribute מקביל,
   * ולכן React לא מכיר בו כ-prop. ה-ref המקומי מזרים את הערך אחרי שהצומת
   * הוצב, וגם ה-ref הציבורי (אם עבר) ממשיך לקבל את אותו element דרך callback ref.
   */
  const internalRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (internalRef.current) internalRef.current.indeterminate = !!indeterminate && !checked;
  }, [indeterminate, checked]);

  const setRefs = (node: HTMLInputElement | null) => {
    internalRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      (ref as React.RefObject<HTMLInputElement | null>).current = node;
    }
  };

  return (
    // stopPropagation keeps row-level click handlers (table rows, declaration rows)
    // from firing again when the checkbox itself is clicked.
    <label
      onClick={(e) => {
        e.stopPropagation();
      }}
      className={`${label} ${className || ''}`}
    >
      {/*
        The input sits ON TOP of the visual box (see checkbox.module.css). Clicks hit it
        directly, so the browser does not synthesize a second, label-forwarded click.
        That duplicate click used to double every checkbox interaction in analytics and
        made PostHog flag routine checkbox use as rage clicks.
      */}
      <input
        type="checkbox"
        ref={setRefs}
        checked={checked}
        disabled={disabled}
        className={input}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        aria-required={ariaRequired}
        data-indeterminate={indeterminate && !checked ? 'true' : undefined}
        onChange={(e) => onCheckedChange?.(e.target.checked)}
        onClick={(e) => {
          e.stopPropagation();
        }}
      />
      <span className={checkbox}></span>
    </label>
  );
}
